import PropTypes from "prop-types";
import { useEffect, useMemo, useRef } from "react";

// Helpers
import { clone } from "@/Common/helpers/Modifiers/formatters";

// Defaults builders
import { DefaultCommissionsTier, OperatingCostsCommissionTier } from "@/PayPlans/helpers/DefaultSections";

// Hooks
import { useCallbackState } from "@/Common/hooks/CallbackState";
import { useValidations } from "@/Common/hooks/Validations";

// Tiers Layouts
import TierAlertError from "@/Layouts/components/Tiers/TierAlertError";
import TierRows from "@/Layouts/components/Tiers/TierRows";
import TierColumnsItem from "@/Layouts/components/Tiers/TierColumnsItem";
import TierColumns from "@/Layouts/components/Tiers/TierColumns";

// Custom components
import MatrixColumn from "./MatrixColumn";
import MatrixRow from "./MatrixRow";

// MUI
import { Grid, Stack, Collapse } from "@mui/material";
import { TransitionGroup } from "react-transition-group";
import { isValidValue } from "@/Common/helpers/Matrices/tiers";

const minimumColumns = 1;
const maximumColumns = 4;
const maximumTiers = Number.MAX_VALUE;

const minimumGrossInput = 500000000;
const maximumGrossInput = 0;

const MIN_GROWTH_INPUT = 10000;
const MAX_GROWTH_INPUT = -100;

export default function OperatingCostsMake(props) {
	const {
		make = "",
		tiers = [],
		readOnly = false,
		disableAddColumn = false,
		onActionChange = () => {},
		onActionUpdate = () => {},
		onActionValidation = () => {},
	} = props;

	// Hooks
	const { errors, isValid, getErrorsMessages, validate } = useValidations();

	const localErrorsMessages = getErrorsMessages();
	const headerErrors = errors?.get("operatingCostGrowth");
	const rowsErrors = errors?.get("costGross");
	const columnsErrors = errors?.get("commissions");
	const rangeError = Boolean(errors?.get("range")?.length);

	// Control to send update actions
	const wasUpdated = useRef(false);

	const onTiersUpdate = (updatedTiers) => {
		onActionValidation?.(make, isValid);
		if (isValid && wasUpdated.current) {
			onActionUpdate?.(make, updatedTiers);
		}
	};

	// // States
	const [innerTiers, setInnerTiers] = useCallbackState([], onTiersUpdate);

	// Methods
	const handleAddColumn = () => {
		const currentTiers = [...innerTiers];
		const commissionInput = currentTiers?.[0]?.["commissions"]?.[0]?.["commission"]?.["input"] || "Percent";
		const lastIndex = currentTiers?.[0]?.["commissions"]?.length ? currentTiers[0]["commissions"].length - 1 : 0;

		currentTiers.forEach((tier) => {
			tier.commissions.push(
				OperatingCostsCommissionTier(0, MAX_GROWTH_INPUT, {
					commission: 0,
					input: commissionInput,
				})
			);
			if (tier?.commissions?.[lastIndex]) {
				tier.commissions[lastIndex]["to"]["value"] = 0;
			}
		});

		wasUpdated.current = false;
		setInnerTiers(currentTiers);
	};

	const handleDeleteColumn = () => {
		const currentTiers = [...innerTiers];
		if (currentTiers?.[0]?.["commissions"]?.length > minimumColumns) {
			currentTiers.forEach((tier) => {
				tier.commissions.splice(-1);
				const lastIndex = tier?.commissions?.length ? tier.commissions.length - 1 : 0;
				tier.commissions[lastIndex]["to"]["value"] = MAX_GROWTH_INPUT;
			});
		}
		update(currentTiers);
	};

	const handleAddTier = () => {
		const currentTiers = [...innerTiers];
		const lastIndex = currentTiers?.length ? currentTiers.length - 1 : 1;
		const lastInput = currentTiers?.[lastIndex]?.["commissions"]?.[0]?.["commission"]?.["input"] || "Percent";
		const previousCommissionsLength = currentTiers?.[lastIndex]?.["commissions"]?.length;

		const commissions = [];
		for (let i = 0; i < previousCommissionsLength; i++) {
			const previousFrom = currentTiers?.[0]?.["commissions"]?.[i]?.["from"]?.["value"] || 0;
			const previousTo = currentTiers?.[0]?.["commissions"]?.[i]?.["to"]?.["value"] || 0;
			commissions[i] = OperatingCostsCommissionTier(previousFrom, previousTo, {
				commission: 0,
				input: lastInput,
			});
		}

		const edgeInput = minimumGrossInput - 0.01;
		currentTiers.push(DefaultCommissionsTier(edgeInput, maximumGrossInput, commissions));

		if (currentTiers?.length) {
			currentTiers[lastIndex].to.value = edgeInput;
		}

		onActionValidation?.(make, false);
		wasUpdated.current = false;

		onActionChange?.(currentTiers.length);
		setInnerTiers(currentTiers);
	};

	const handleDeleteTier = () => {
		const currentTiers = [...innerTiers];
		if (currentTiers.length >= 1) {
			currentTiers.splice(-1);
		}
		const lastIndex = currentTiers?.length ? currentTiers.length - 1 : 1;
		currentTiers[lastIndex].to.value = maximumGrossInput;
		onActionChange?.(currentTiers.length);
		update(currentTiers);
	};

	const handleUpdateInput = (_, __, location, area, input) => {
		const currentTiers = [...innerTiers];
		switch (location) {
			case "column":
				break;
			case "row":
				currentTiers.forEach((tier) => {
					tier.commissions?.forEach((commission) => {
						if (area in commission) {
							commission[area]["input"] = input;
							commission[area]["value"] = 0;
						}
					});
				});
				break;
			default:
				break;
		}
		update(currentTiers);
	};

	const handleUpdateValue = (row, column, location, area, value) => {
		const updatedValue = value ? Number(value) : 0;
		let currentTiers = [];

		switch (location) {
			case "column":
				currentTiers = updateValueColumn({ column, area, updatedValue });
				break;
			case "row":
				currentTiers = updateValueRow({ row, column, area, updatedValue });
				break;
			default:
				break;
		}

		update(currentTiers);
	};

	const updateValueColumn = (args) => {
		const { column, area, updatedValue } = args;
		const currentTiers = [...innerTiers];

		currentTiers.forEach((tier) => {
			tier.commissions?.forEach((commission, key) => {
				if (area in commission && key === column) {
					commission[area]["value"] = updatedValue;
					if (tier.commissions?.[key + 1]) {
						tier.commissions[key + 1]["from"]["value"] = updatedValue - 0.01;
					}
				}
			});
		});

		return currentTiers;
	};

	const updateValueRow = (args) => {
		const { row, column, area, updatedValue } = args;
		const currentTiers = [...innerTiers];

		if (area !== "commission") {
			currentTiers[row][area]["value"] = updatedValue;
			if (currentTiers?.[row + 1] && area === "to") {
				currentTiers[row + 1]["from"]["value"] = updatedValue - 0.01;
			}
		} else {
			currentTiers[row]["commissions"][column][area]["value"] = updatedValue;
		}

		return currentTiers;
	};

	const update = (updatedTiers = []) => {
		wasUpdated.current = true;
		setInnerTiers(updatedTiers);
	};

	useMemo(() => {
		if (innerTiers?.length) {
			validate([
				{
					definition: {
						useKey: "costGross",
						operators: {
							conditions: [
								{
									type: "INLINE",
									on: "value",
									compare: ">",
								},
								{
									type: "STEP",
									on: "value",
									compare: ">",
								},
							],
						},
						errorsMap: ["from", "to"],
						message: "Invalid tiers",
						validationType: "DOUBLE_INPUT",
						specialProperties: {
							noFirstError: true,
							noLastError: true,
						},
					},
					section: innerTiers || [],
				},
				{
					definition: {
						useKey: "operatingCostGrowth",
						operators: {
							conditions: [
								{
									type: "INLINE",
									on: "value",
									compare: ">",
								},
								{
									type: "STEP",
									on: "value",
									compare: ">",
								},
							],
						},
						errorsMap: ["from", "to"],
						message: "Volume invalid tiers",
						validationType: "DOUBLE_INPUT",
						specialProperties: {
							noFirstError: true,
							noLastError: true,
						},
					},
					section: innerTiers?.[0]?.commissions || [],
				},
				{
					definition: {
						useKey: "commissions",
						operators: {
							conditions: [
								{
									order: "ROWS",
									type: "STEP",
									on: "value",
									compare: "<",
								},
								{
									order: "COLUMNS",
									type: "STEP",
									on: "value",
									compare: "<",
									deltas: { Dollar: 0, Percent: 0 },
								},
							],
						},
						errorsMap: ["commission"],
						errorsMapOn: "commissions",
						message: "Incorrect commissions definition",
						validationType: "MATRIX",
					},
					section: innerTiers,
				},
				{
					definition: {
						useKey: "range",
						errorsMap: ["commission"],
						message: "Incorrect commissions definition",
						validationType: "CUSTOM",
						operators: {
							customFn: (params = null) => {
								const { checkTiers = [] } = params || {};
								const isValueValid = isValidValue(checkTiers, { value: 0, operator: "<=" });
								return [{ commission: !isValueValid }];
							},
							params: {
								checkTiers: innerTiers || [],
							},
						},
					},
					section: innerTiers,
				},
			]);
		}
	}, [innerTiers, validate]);

	useEffect(() => {
		setInnerTiers(clone(tiers));
		return () => {
			wasUpdated.current = false;
			setInnerTiers([]);
		};
	}, [tiers, setInnerTiers]);

	return (
		<Grid container item mt={2} xs={12}>
			<Grid item xs={12}>
				<TierColumns
					titleLabel={"Operating Cost Growth"}
					titleContainerProps={{
						justifyContent: "flex-start",
						alignItems: "end",
						sx: { maxWidth: "241px", width: "241px", marginRight: "3px" },
					}}
					titleProps={{
						className: "tiered-title tiered-title-adjustment",
						sx: null,
					}}
					disabledDelete={innerTiers?.[0]?.commissions?.length <= minimumColumns || readOnly}
					disabledAdd={disableAddColumn || innerTiers?.[0]?.commissions?.length >= maximumColumns || readOnly}
					onClickDelete={() => handleDeleteColumn()}
					onClickAdd={() => handleAddColumn()}
				>
					<Stack direction={"row"} position={"relative"}>
						{innerTiers?.[0]?.commissions?.map((tier, key) => {
							const fromDisabled = Boolean(!key);
							const toDisabled = Boolean(innerTiers?.[0]?.commissions.length === key + 1);
							const tierKey = `column-${key}`;

							return (
								<TierColumnsItem key={tierKey} index={key} title={"Bonus"}>
									<MatrixColumn
										index={key}
										item={tier}
										errors={headerErrors}
										fromDisabled={fromDisabled}
										toDisabled={toDisabled}
										readOnly={readOnly}
										onActionUpdate={handleUpdateValue}
									/>
								</TierColumnsItem>
							);
						})}
					</Stack>
				</TierColumns>
			</Grid>

			<Grid item xs={12} mt={1} minHeight={"135px"}>
				<TierRows
					showTitle
					title={"Cost Gross"}
					sxTitlePropsContainer={{
						top: 105,
					}}
					centerTitleAfterRow={2}
					deleteButtonText={"Remove Tier"}
					addButtonText={"Add Tier"}
					deleteButtonDisabled={innerTiers?.length <= 1 || readOnly}
					addButtonDisabled={innerTiers?.length >= maximumTiers || readOnly}
					spaces={{
						mainSection: 235,
						columns: 195,
						totalColumns: innerTiers?.[0]?.commissions?.length || 1,
						totalRows: innerTiers?.length || 1,
						separation: 5.5,
					}}
					rowsAreaProps={{
						ml: "35px",
					}}
					onClickDelete={() => handleDeleteTier()}
					onClickAdd={() => handleAddTier()}
				>
					<TransitionGroup>
						<Collapse>
							<Stack direction="column" rowGap={2}>
								{innerTiers?.map((tier, key) => {
									const fromDisabled = Boolean(!key);
									const toDisabled = Boolean(innerTiers?.length === key + 1);
									const commissionsLength = innerTiers?.[0]?.commissions?.length || 0;
									const reduction = commissionsLength * 10 + 45 + commissionsLength;
									const tierKey = `row-${key}`;

									return (
										<Grid key={tierKey} container item>
											<MatrixRow
												index={key}
												item={tier}
												fromDisabled={fromDisabled}
												toDisabled={toDisabled}
												spaces={{
													mainSection: 235,
													columns: 195,
													totalColumns: innerTiers?.[0]?.commissions?.length || 1,
													totalRows: innerTiers?.length || 1,
													separation: 5.5,
													reduction,
												}}
												rowsErrors={rowsErrors || []}
												columnsErrors={columnsErrors || []}
												firstCommissionError={rangeError}
												readOnly={readOnly}
												spacing={"10.5px"}
												sxMainContent={{ minWidth: "235.5px", marginRight: "-13px" }}
												sxCells={{ minWidth: "184.42px" }}
												onActionUpdate={handleUpdateValue}
												onChangeType={handleUpdateInput}
											/>
										</Grid>
									);
								})}
							</Stack>
						</Collapse>
					</TransitionGroup>

					{Boolean(localErrorsMessages?.length) && (
						<Grid container direction="column" rowSpacing={0.2} mt={1} mb={1} alignItems="flex-start">
							{localErrorsMessages.map((message, key) => (
								<TierAlertError key={`error_operating_cost_make_${message}_${key}`} text={message} />
							))}
						</Grid>
					)}
				</TierRows>
			</Grid>
		</Grid>
	);
}
OperatingCostsMake.propTypes = {
	make: PropTypes.string,
	tiers: PropTypes.array,
	readOnly: PropTypes.bool,
	disableAddColumn: PropTypes.bool,
	onActionChange: PropTypes.func,
	onActionUpdate: PropTypes.func,
	onActionValidation: PropTypes.func,
};
